import { useCallback, useRef, useState } from "react";
import { WHATSAPP_VIDEO_MIME_TYPES } from "../utils/videoMimeUtils";

export type MediaRecorderError =
  | "permission_denied"
  | "not_supported"
  | "unknown";

const VIDEO_BITS_PER_SECOND = 2_500_000;

const pickMimeType = (withVideo: boolean): string => {
  const types = withVideo
    ? WHATSAPP_VIDEO_MIME_TYPES
    : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
};

const mapMediaError = (err: unknown): MediaRecorderError => {
  if (err instanceof DOMException) {
    if (
      err.name === "NotAllowedError" ||
      err.name === "PermissionDeniedError"
    ) {
      return "permission_denied";
    }
    if (err.name === "NotSupportedError") {
      return "not_supported";
    }
  }
  return "unknown";
};

export const useMediaRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<MediaRecorderError | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const reset = useCallback(
    (options?: { keepStream?: boolean }) => {
      recorderRef.current = null;
      chunksRef.current = [];
      setRecordedBlob(null);
      setError(null);
      setIsRecording(false);
      if (!options?.keepStream) stopTracks();
    },
    [stopTracks],
  );

  const recordExistingStream = useCallback(
    (stream: MediaStream, withVideo: boolean) => {
      setRecordedBlob(null);
      setError(null);
      chunksRef.current = [];

      const mimeType = pickMimeType(withVideo);
      if (!mimeType) {
        setError("not_supported");
        return;
      }

      const recorderOptions = withVideo
        ? { mimeType, videoBitsPerSecond: VIDEO_BITS_PER_SECOND }
        : { mimeType };
      const recorder = new MediaRecorder(stream, recorderOptions);
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setIsRecording(false);
      };

      recorder.start(100);
      setIsRecording(true);
    },
    [],
  );

  const startVideoRecording = useCallback(async () => {
    reset();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;

      const mimeType = pickMimeType(true);
      if (!mimeType) {
        setError("not_supported");
        stopTracks();
        return null;
      }

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setIsRecording(false);
        stopTracks();
      };

      recorder.start(100);
      setIsRecording(true);
      return stream;
    } catch (err) {
      setError(mapMediaError(err));
      stopTracks();
      return null;
    }
  }, [reset, stopTracks]);

  const startAudioRecording = useCallback(async () => {
    reset();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickMimeType(false);
      if (!mimeType) {
        setError("not_supported");
        stopTracks();
        return null;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setIsRecording(false);
        stopTracks();
      };

      recorder.start(100);
      setIsRecording(true);
      return stream;
    } catch (err) {
      setError(mapMediaError(err));
      stopTracks();
      return null;
    }
  }, [reset, stopTracks]);

  const stopRecording = useCallback(() => {
    if (
      recorderRef.current &&
      recorderRef.current.state !== "inactive"
    ) {
      recorderRef.current.stop();
    }
  }, []);

  return {
    isRecording,
    recordedBlob,
    error,
    startVideoRecording,
    startAudioRecording,
    recordExistingStream,
    stopRecording,
    reset,
    stopTracks,
  };
};
